import { useContext, useEffect, useMemo } from "react";
import { DynamicFormFullContext } from "../Providers/DynamicFormFullContextProvider";
import { DynamicOption, IDynamicFormFullContext } from "../DynamicFormContracts";
import useSWR from "swr";

export interface IuseDynamicOptionsProps {
  options?: DynamicOption[];
  optionsUrl?: string;
  optionsUrlQuery?: { [key: string]: string };
}

export default function useDynamicOptions<T = any>(
  props: IuseDynamicOptionsProps
): [DynamicOption[], boolean, boolean] {

  const context: IDynamicFormFullContext<T> = useContext(DynamicFormFullContext);
  if (!context) throw new Error("useDynamicFormValue should be used inside Dynamic Form");

  const fullUrl = typeof document === "undefined"
    ? ""
    : (() => {
      if (!props.optionsUrl) return null;
      const urlObj = new URL(
        props.optionsUrl,
        props.optionsUrl.startsWith("http://") || props.optionsUrl.startsWith("https://")
          ? undefined
          : document.location.origin + "/"
      );

      if (!props.optionsUrlQuery) return urlObj.toString();

      for (let queryName in props.optionsUrlQuery) {
        const fieldName = props.optionsUrlQuery[queryName];
        const fieldValue = (context.state.value as any)[fieldName];
        if (fieldValue == null || fieldValue === "") return "";
        urlObj.searchParams.append(queryName, fieldValue.toString());
      }
      return urlObj.toString();
    })();

  const { data, isValidating, error } = useSWR(fullUrl, url => {
    if (!url) return Promise.resolve([]);
    if (!context.optionsFetcher) {
      console.error("optionsFetcher Not found in DynamicForm context");
      throw new Error("optionsFetcher Not found in DynamicForm context");
    }
    return context.optionsFetcher(url);
  });

  const options = useMemo(() => props.options ?? data ?? [], [data, props.options]);
  const loading = useMemo(() => !props.options && !data && isValidating, [isValidating, data]);

  useEffect(() => { (props as any).__fetchedOptions__ = options; }, [options]);

  return [options, loading, error!!];
}
